import { useEffect, createElement, ComponentType } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { ThemeName } from '@/utils/storage';
import { NativeFeatures } from '@/services/nativeFeatures';
import Starfield from '@/components/backgrounds/Starfield';
import OceanBackground from '@/components/backgrounds/OceanBackground';
import RainbowBackground from '@/components/backgrounds/RainbowBackground';
import ForestBackground from '@/components/backgrounds/ForestBackground';
import SunsetBackground from '@/components/backgrounds/SunsetBackground';
import RobotBackground from '@/components/backgrounds/RobotBackground';
import VolcanoBackground from '@/components/backgrounds/VolcanoBackground';
import MonsterTruckBackground from '@/components/backgrounds/MonsterTruckBackground';
import NinjaBackground from '@/components/backgrounds/NinjaBackground';
import SpongeBobBackground from '@/components/backgrounds/SpongeBobBackground';
import {
  CandyBackground,
  DinosaurBackground,
} from '@/components/backgrounds/PlayfulBackgrounds';

// Single source of truth mapping each theme to its background component.
// Typed as a full Record so adding a ThemeName without a background fails to compile.
const themeBackgrounds: Record<ThemeName, ComponentType> = {
  robots: RobotBackground,
  unicorn: RainbowBackground,
  space: Starfield,
  ocean: OceanBackground,
  forest: ForestBackground,
  sunset: SunsetBackground,
  volcano: VolcanoBackground,
  trucks: MonsterTruckBackground,
  ninja: NinjaBackground,
  spongebob: SpongeBobBackground,
  dinosaurs: DinosaurBackground,
  candy: CandyBackground,
};

// Keep track of the React root so we can unmount it properly
let backgroundRoot: Root | null = null;

export const useThemeManager = (theme: ThemeName | undefined) => {
  useEffect(() => {
    if (theme) {
      applyTheme(theme);
    }
    
    // Clean up function for when component unmounts or theme changes
    return () => {
      // Unmount React root
      if (backgroundRoot) {
        backgroundRoot.unmount();
        backgroundRoot = null;
      }

      const existingBg = document.getElementById('theme-background');
      if (existingBg) {
        existingBg.remove();
      }
      const transitionOverlay = document.getElementById('theme-transition-overlay');
      if (transitionOverlay) {
        transitionOverlay.remove();
      }
    };
  }, [theme]);

  const applyTheme = (theme: ThemeName) => {
    // Create transition overlay
    createTransitionOverlay();
    
    // Set transition class
    document.body.classList.add('theme-transition-active');
    
    // Apply theme changes after a short delay
    setTimeout(() => {
      // Apply theme color and class
      document.documentElement.style.setProperty('--primary', getThemeColor(theme));
      document.documentElement.style.setProperty(
        '--primary-foreground',
        getPrimaryForeground(theme)
      );
      Array.from(document.body.classList)
        .filter((className) => className.startsWith('theme-'))
        .forEach((className) => document.body.classList.remove(className));
      document.body.classList.add(`theme-${theme}`);
      
      // Clean up any existing theme-specific backgrounds
      const existingBg = document.getElementById('theme-background');
      if (existingBg) {
        existingBg.remove();
      }
      
      // Add theme-specific background component
      renderBackgroundComponent(themeBackgrounds[theme]);

      // Apply dark mode for specific themes
      const isDarkTheme =
        theme === 'space' ||
        theme === 'robots' ||
        theme === 'volcano' ||
        theme === 'trucks' ||
        theme === 'ninja';
      if (isDarkTheme) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      
      // Set status bar theme to match app theme
      NativeFeatures.setStatusBarTheme(isDarkTheme);
      
      // Remove transition class after theme is applied
      setTimeout(() => {
        document.body.classList.remove('theme-transition-active');
        
        // Remove transition overlay after transition completes
        setTimeout(() => {
          const transitionOverlay = document.getElementById('theme-transition-overlay');
          if (transitionOverlay) {
            transitionOverlay.remove();
          }
        }, 500);
      }, 300);
    }, 100);
  };

  const createTransitionOverlay = () => {
    // Remove any existing overlay
    const existingOverlay = document.getElementById('theme-transition-overlay');
    if (existingOverlay) {
      existingOverlay.remove();
    }
    
    // Create new overlay
    const overlay = document.createElement('div');
    overlay.id = 'theme-transition-overlay';
    overlay.className = 'theme-transition-overlay';
    document.body.appendChild(overlay);
  };

  const renderBackgroundComponent = (Component: ComponentType) => {
    // Unmount existing background if any
    if (backgroundRoot) {
      backgroundRoot.unmount();
      backgroundRoot = null;
    }

    // Create a container for the background
    const bgDiv = document.createElement('div');
    bgDiv.id = 'theme-background';
    bgDiv.style.position = 'fixed';
    bgDiv.style.top = '0';
    bgDiv.style.left = '0';
    bgDiv.style.width = '100%';
    bgDiv.style.height = '100%';
    bgDiv.style.zIndex = '-1';
    bgDiv.style.overflow = 'hidden';
    bgDiv.style.pointerEvents = 'none';

    document.body.appendChild(bgDiv);

    // Render the component using React
    try {
      backgroundRoot = createRoot(bgDiv);
      backgroundRoot.render(createElement(Component));
    } catch (error) {
      console.error("Error rendering background:", error);
    }
  };

  const getThemeColor = (theme: ThemeName): string => {
    switch (theme) {
      case 'robots': return '199 89% 48%';
      case 'unicorn': return '286 81% 60%';
      case 'space': return '246 84% 58%';
      case 'ocean': return '187 91% 37%';
      case 'forest': return '122 39% 49%';    // Forest green
      case 'sunset': return '24 100% 50%';    // Sunset orange
      case 'volcano': return '12 88% 48%';    // Lava red-orange
      case 'trucks': return '82 78% 45%';     // Lime green
      case 'ninja': return '0 74% 42%';       // Crimson
      case 'spongebob': return '48 96% 53%';  // Sponge yellow
      case 'dinosaurs': return '142 72% 29%';
      case 'candy': return '330 81% 47%';
      default: return '204 80% 48%';
    }
  };

  const getPrimaryForeground = (theme: ThemeName): string =>
    theme === 'spongebob' || theme === 'trucks' ? '222.2 84% 4.9%' : '210 40% 98%';

  const getThemeEmoji = (themeName: ThemeName | undefined): string => {
    if (!themeName) return '⭐';

    switch (themeName) {
      case 'robots': return '🤖';
      case 'unicorn': return '🦄';
      case 'space': return '🚀';
      case 'ocean': return '🌊';
      case 'forest': return '🌳';
      case 'sunset': return '🌅';
      case 'volcano': return '🌋';
      case 'trucks': return '🏎️';
      case 'ninja': return '🥷';
      case 'spongebob': return '🧽';
      case 'dinosaurs': return '🦖';
      case 'candy': return '🍭';
      default: return '⭐';
    }
  };

  return {
    applyTheme,
    getThemeEmoji
  };
};